import React from "react";
import { AlertCircle, CheckCircle, Clock, XCircle, User } from "lucide-react";

/**
 * Status color mapping for different entity types
 */
export function getStatusColor(status: string, entityType: string): string {
  const statusLower = status.toLowerCase();

  switch (entityType) {
    case "contacts":
      switch (statusLower) {
        case "customer":
          return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800";
        case "prospect":
          return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
        case "lead":
          return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800";
        case "churned":
          return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
        case "inactive":
          return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
        default:
          return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
      }
    case "tickets":
      switch (statusLower) {
        case "new":
          return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
        case "assigned":
          return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800";
        case "in_progress":
          return "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800";
        case "resolved":
          return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800";
        case "closed":
          return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
        case "cancelled":
          return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
        default:
          return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
      }
    case "tasks":
      switch (statusLower) {
        case "pending":
          return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800";
        case "in_progress":
          return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
        case "completed":
          return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800";
        case "cancelled":
          return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
        default:
          return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
      }
    default:
      return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
  }
}

/**
 * Priority color mapping
 */
export function getPriorityColor(priority: string): string {
  const priorityLower = priority.toLowerCase();
  switch (priorityLower) {
    case "critical":
    case "urgent":
      return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
    case "high":
      return "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800";
    case "medium":
      return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800";
    case "low":
      return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800";
    default:
      return "bg-card-50 text-gray-700 border-gray-200 dark:text-gray-300 dark:border-gray-700";
  }
}

/**
 * Get status icon based on status and entity type
 */
export function getStatusIcon(status: string, entityType: string): React.ReactNode {
  const statusLower = status.toLowerCase();

  switch (entityType) {
    case "contacts":
      switch (statusLower) {
        case "customer":
          return <CheckCircle className="h-3 w-3" />;
        case "prospect":
          return <Clock className="h-3 w-3" />;
        case "lead":
          return <User className="h-3 w-3" />;
        case "churned":
          return <XCircle className="h-3 w-3" />;
        default:
          return <User className="h-3 w-3" />;
      }
    case "tickets":
      switch (statusLower) {
        case "new":
          return <AlertCircle className="h-3 w-3" />;
        case "assigned":
          return <User className="h-3 w-3" />;
        case "in_progress":
          return <Clock className="h-3 w-3" />;
        case "resolved":
          return <CheckCircle className="h-3 w-3" />;
        case "closed":
          return <CheckCircle className="h-3 w-3" />;
        default:
          return <AlertCircle className="h-3 w-3" />;
      }
    default:
      return <User className="h-3 w-3" />;
  }
}
